import type { ProspectStatus, UpdateProspectBody } from '@api/models';
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { detailPollInterval, emailActivity, pollFastAfterAction, queuePollInterval } from '@website/lib/prospects';
import {
  type ProspectFilters,
  type ProspectScope,
  cancelProspectEmails,
  createGmailConnectUrl,
  getGmailConnection,
  getProspect,
  getProspectCounts,
  getProspectQueue,
  listProspects,
  sendProspectEmails,
  setProspectDraft,
  setProspectStatus,
  updateProspect
} from '@website/services/prospects.service';

const KEY = ['prospects'] as const;

export function useProspects(filters: ProspectFilters) {
  return useInfiniteQuery({
    queryKey: [...KEY, 'list', filters],
    queryFn: ({ pageParam }) => listProspects(filters, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.cursor ?? undefined,
    // Switching tab or typing a search keeps the current rows on screen until the next ones arrive.
    placeholderData: keepPreviousData,
    refetchInterval: (query) =>
      queuePollInterval(
        emailActivity(query.state.data?.pages.flatMap((page) => page.data.map((p) => p.lastEmail)) ?? [])
      )
  });
}

export const useProspect = (id: string | null) =>
  useQuery({
    queryKey: [...KEY, 'detail', id],
    queryFn: () => getProspect(id as string),
    enabled: Boolean(id),
    retry: false,
    refetchInterval: (query) => detailPollInterval(query.state.data?.emails)
  });

/** Loads a prospect into the cache before it is opened (the sheet's neighbours), so stepping shows no skeleton. */
export function usePrefetchProspect() {
  const client = useQueryClient();
  return useCallback(
    (id: string) => client.prefetchQuery({ queryKey: [...KEY, 'detail', id], queryFn: () => getProspect(id) }),
    [client]
  );
}

export const useGmailConnection = () => useQuery({ queryKey: [...KEY, 'gmail'], queryFn: getGmailConnection });

export function useSetProspectDraft() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, subject, body }: { id: string; subject: string; body: string }) =>
      setProspectDraft(id, { subject, body }),
    onSuccess: () => client.invalidateQueries({ queryKey: KEY })
  });
}

export function useUpdateProspect() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...fields }: { id: string } & UpdateProspectBody) => updateProspect(id, fields),
    onSuccess: () => client.invalidateQueries({ queryKey: KEY })
  });
}

export function useSetProspectStatus() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: ProspectStatus }) => setProspectStatus(id, status),
    onSuccess: () => client.invalidateQueries({ queryKey: KEY })
  });
}

/** How many prospects each tab holds: refreshed with the list (every prospects mutation invalidates both). */
export const useProspectCounts = (scope: ProspectScope) =>
  useQuery({
    queryKey: [...KEY, 'counts', scope],
    queryFn: () => getProspectCounts(scope),
    placeholderData: keepPreviousData,
    // As fast as the list while emails are on their way, so "in progress" empties with it.
    refetchInterval: (query) => queuePollInterval({ sending: false, queued: (query.state.data?.in_progress ?? 0) > 0 })
  });

/** The calling admin's queue: daily usage, the pace, and when it should be done. */
export const useProspectQueue = () =>
  useQuery({
    queryKey: [...KEY, 'queue'],
    queryFn: getProspectQueue,
    refetchInterval: (query) =>
      queuePollInterval({
        sending: (query.state.data?.sendingNow ?? 0) > 0,
        queued: (query.state.data?.queuedMine ?? 0) > 0
      })
  });

/** Refetches at once on success and polls fast for a while: the rows show `queued`, then go out. */
export function useSendProspectEmails() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: sendProspectEmails,
    onSuccess: () => {
      pollFastAfterAction();
      return client.invalidateQueries({ queryKey: KEY });
    }
  });
}

export function useCancelProspectEmails() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: cancelProspectEmails,
    onSuccess: () => {
      pollFastAfterAction();
      return client.invalidateQueries({ queryKey: KEY });
    }
  });
}

export const useConnectGmail = () =>
  useMutation({
    mutationFn: createGmailConnectUrl,
    onSuccess: ({ url }) => {
      window.location.href = url;
    }
  });
